import loadScript, { URLS } from '@crehana/utils/dist/loadScript';

import I18N_STRINGS from 'Jsx/CheckoutV4/i18n/language';

import { CardHolderSate } from '../../../UserInputFields/CardHolderInput/CardHolderInput';
import { CreditCardState } from '../../../UserInputFields/CreditCardInput/CreditCardInput';
import { DocumentState } from '../../../UserDataFields/DocumentInput';
import { DLocalData } from '../components/DLocal';

import {
  DLocalError,
  getConektaToken,
  getDLocalToken,
  getEbanxToken,
  getPayUToken,
  getStripeMexicoToken,
  getStripeToken,
  StripeError,
  TOKEN_ERROR,
} from './tokens';

import { EbanxResponse, PayUResponse, TokenResponse } from './tokens/types';

export async function createDLocalToken(
  dLocalCheckoutData: DLocalData | undefined,
  cardHolder: CardHolderSate | undefined,
): Promise<TokenResponse> {
  try {
    if (!dLocalCheckoutData || !cardHolder)
      throw new Error('Internal error: No DLocal data.');
    const { token } = await getDLocalToken({
      dLocalData: dLocalCheckoutData,
      cardHolder: cardHolder.name,
    });

    return { token };
  } catch (error) {
    const errorMessage =
      error instanceof DLocalError && error.code !== TOKEN_ERROR
        ? error.message
        : I18N_STRINGS.CATCH_MESSAGE;

    return { token: '', error: { message: errorMessage } };
  }
}

export async function createConektaToken({
  creditCard,
  cardHolder,
}: {
  creditCard: CreditCardState | undefined;
  cardHolder: CardHolderSate | undefined;
}): Promise<TokenResponse> {
  try {
    if (
      !creditCard ||
      !cardHolder ||
      !creditCard.isValid ||
      !cardHolder.isValid ||
      !window.CONEKTA_PUBLIC_KEY
    )
      throw new Error('Invalid Credit Card');

    if (!window.Conekta) {
      await loadScript({ src: URLS.conekta });
    }

    const { token } = await getConektaToken({ creditCard, cardHolder });

    return { token };
  } catch (error) {
    const errorMessage =
      error instanceof StripeError ? error.message : I18N_STRINGS.CATCH_MESSAGE;

    return { token: '', error: { message: errorMessage } };
  }
}

export async function createEbanxToken({
  creditCard,
  cardHolder,
  countryPrefix,
}: {
  creditCard: CreditCardState | undefined;
  cardHolder: CardHolderSate | undefined;
  countryPrefix: string;
}): Promise<EbanxResponse> {
  try {
    if (
      !creditCard ||
      !cardHolder ||
      !creditCard.isValid ||
      !cardHolder.isValid ||
      !window.EBANX_PUBLIC_INTEGRATION_KEY
    )
      throw new Error('Invalid Credit Card');

    if (!window.EBANX) {
      await loadScript({ src: URLS.ebanx });
    }

    const { token, maskedCardNumber, deviceId, error } = await getEbanxToken({
      creditCard,
      cardHolder,
      countryPrefix,
    });

    if (error) {
      return {
        token: '',
        deviceId: '',
        maskedCardNumber: '',
        error: { message: I18N_STRINGS.CATCH_MESSAGE },
      };
    }
    return { token, maskedCardNumber, deviceId };
  } catch (error) {
    return {
      token: '',
      deviceId: '',
      maskedCardNumber: '',
      error: { message: I18N_STRINGS.CATCH_MESSAGE },
    };
  }
}

export async function createPayUToken({
  creditCard,
  cardHolder,
  document,
}: {
  creditCard: CreditCardState | undefined;
  cardHolder: CardHolderSate | undefined;
  document: DocumentState | undefined;
}): Promise<PayUResponse> {
  try {
    if (
      !creditCard ||
      !cardHolder ||
      !document ||
      !creditCard.isValid ||
      !cardHolder.isValid ||
      !document.isValid
    )
      throw new Error('Invalid Credit Card');

    const { PAYU_PUBLIC_KEY, PAYU_SET_URL_API, PAYU_ACCOUNT_PE } = window;

    if (PAYU_PUBLIC_KEY === 'None' || !PAYU_SET_URL_API || !PAYU_ACCOUNT_PE)
      throw new Error('Invalid PAY U Public Key');

    if (!window.payU) {
      await loadScript({ src: URLS.payu }).then(res => {
        if (res) {
          window.payU.setURL(PAYU_SET_URL_API);
          window.payU.setPublicKey(PAYU_PUBLIC_KEY);
          window.payU.setAccountID(PAYU_ACCOUNT_PE);
          window.payU.setListBoxID('mylistID');
          window.payU.setLanguage('es');
          window.payU.getPaymentMethods();
        }
      });
    }

    const { token, error } = await getPayUToken({
      creditCard,
      cardHolder,
      document,
    });

    if (error)
      return {
        token: '',
        cardBrand: '',
        error: { message: error || I18N_STRINGS.CATCH_MESSAGE },
      };

    return {
      token,
      cardBrand: window.payU.cardPaymentMethod(creditCard?.number),
    };
  } catch (error) {
    return {
      token: '',
      cardBrand: '',
      error: { message: I18N_STRINGS.CATCH_MESSAGE },
    };
  }
}

export async function createStripeToken({
  creditCard,
}: {
  creditCard: CreditCardState | undefined;
}): Promise<TokenResponse> {
  try {
    if (!creditCard || !creditCard.isValid)
      throw new Error('Invalid Credit Card');

    const { token, error } = await getStripeToken(creditCard);

    if (error)
      return {
        error: { message: error || I18N_STRINGS.CATCH_MESSAGE },
        token: '',
      };

    return { token };
  } catch (error) {
    const errorMessage =
      error instanceof StripeError ? error.message : I18N_STRINGS.CATCH_MESSAGE;

    return { token: '', error: { message: errorMessage } };
  }
}

export async function createStripeMexicoToken({
  creditCard,
}: {
  creditCard: CreditCardState | undefined;
}): Promise<TokenResponse> {
  try {
    if (!creditCard || !creditCard.isValid)
      throw new Error('Invalid Credit Card');

    const { token, error } = await getStripeMexicoToken(creditCard);

    if (error)
      return {
        error: { message: error || I18N_STRINGS.CATCH_MESSAGE },
        token: '',
      };

    return { token };
  } catch (error) {
    const errorMessage =
      error instanceof StripeError ? error.message : I18N_STRINGS.CATCH_MESSAGE;

    return { token: '', error: { message: errorMessage } };
  }
}

const checksum = (s: string) => {
  let hash = 0;
  const strlen = s.length;

  if (strlen === 0) {
    return hash;
  }

  for (let i = 0; i < strlen; i += 1) {
    const c = s.charCodeAt(i);

    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + c;
    // eslint-disable-next-line no-bitwise
    hash &= hash; // Convert to 32bit integer
  }

  return Math.abs(hash);
};

export const generateDeviceId = () => {
  const nav = window.navigator;
  const { screen } = window;
  let deviceId: string = nav.mimeTypes.length.toString();

  Object.values(navigator.mimeTypes).forEach(p => {
    deviceId += p.type;
  });
  deviceId += nav.userAgent.replace(/\D+/g, '');
  deviceId += nav.plugins.length.toString();

  Object.values(navigator.plugins).forEach(p => {
    deviceId += p.filename;
  });

  deviceId += screen.height.toString() || '';
  deviceId += screen.width.toString() || '';
  deviceId += screen.pixelDepth.toString() || '';

  return checksum(deviceId);
};
